// swish'd — Supabase Storage Layer
// Place this file at: src/supabaseStorage.js

import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || 'https://iqeesgevjhxuxykcwkro.supabase.co',
  process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlxZWVzZ2V2amh4dXh5a2N3a3JvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzczODY0NTMsImV4cCI6MjA5Mjk2MjQ1M30.l9pMGdJDAWZm_jjpQElBSLw07qT5JaiSGE8TTgcilcU'
);

// Return the authenticated user's Supabase Auth UUID.
// Unifies identity across the whole storage layer on the auth user, so
// profile/quiz/impression rows all key to the same user_id. Returns null
// when there is no active session (callers should be authenticated).
export async function getUserId() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id || null;
}

// ── PROFILE ──────────────────────────────────────────────
// Upload a profile photo to the public `profile-photos` bucket at `${uid}/avatar`
// (upsert overwrites the user's previous avatar) and return its public URL.
// Returns null when unauthenticated or on upload error.
export async function uploadProfilePhoto(file) {
  const uid = await getUserId();
  if (!uid) return null;
  const path = `${uid}/avatar`;
  const { error } = await supabase.storage.from('profile-photos').upload(path, file, { upsert: true });
  if (error) { console.error('photo upload failed', error); return null; }
  const { data } = supabase.storage.from('profile-photos').getPublicUrl(path);
  return data.publicUrl;
}

export async function saveProfile(profileData, lookingFor) {
  const uid = await getUserId();
  if (!uid) return false;
  const { error } = await supabase
    .from('swishd_profiles')
    .upsert({
      user_id: uid,
      name: profileData.name,
      user_name: profileData.user_name,
      dob: profileData.dob,
      occupation: profileData.occupation,
      personality: profileData.personality,
      location: profileData.location,
      photo_url: profileData.photo_url,
      looking_for: lookingFor,
      updated_at: new Date().toISOString()
    }, { onConflict: 'user_id' });
  return !error;
}

export async function loadProfile() {
  const uid = await getUserId();
  if (!uid) return null;
  const { data } = await supabase
    .from('swishd_profiles')
    .select('*')
    .eq('user_id', uid)
    .single();
  return data;
}

// ── IMPRESSIONS ───────────────────────────────────────────
export async function saveImpression(imp) {
  const uid = await getUserId();
  if (!uid) return null;
  const row = {
    user_id: uid,
    drink: imp.wine,
    venue: imp.venue,
    date_visited: imp.date,
    with_person: imp.with,
    notes: imp.notes,
    details: imp.details,
    rating: imp.rating || 0,
  };
  // Bottle scan fields — only set when present, so menu-scan rows stay null
  if (imp.vintage  != null && imp.vintage  !== '') row.vintage  = imp.vintage;
  if (imp.region   != null && imp.region   !== '') row.region   = imp.region;
  if (imp.country  != null && imp.country  !== '') row.country  = imp.country;
  if (imp.varietal != null && imp.varietal !== '') row.varietal = imp.varietal;
  if (imp.abv      != null && imp.abv      !== '') row.abv      = imp.abv;
  // Link to canonical extracted item from menu_items
  if (imp.menu_item_id) row.menu_item_id = imp.menu_item_id;
  const { data, error } = await supabase
    .from('swishd_impressions')
    .insert(row)
    .select()
    .single();
  return error ? null : data;
}

export async function loadImpressions() {
  const uid = await getUserId();
  if (!uid) return [];
  const { data } = await supabase
    .from('swishd_impressions')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  return (data || []).map(d => ({
    id: d.id,
    wine: d.drink,
    venue: d.venue,
    date: d.date_visited,
    with: d.with_person,
    notes: d.notes,
    details: d.details,
    rating: d.rating,
    vintage: d.vintage,
    region: d.region,
    country: d.country,
    varietal: d.varietal,
    abv: d.abv,
  }));
}

export async function deleteImpression(id) {
  const { error } = await supabase
    .from('swishd_impressions')
    .delete()
    .eq('id', id);
  return !error;
}

export async function updateImpression(id, imp) {
  const patch = {
    drink: imp.wine,
    venue: imp.venue,
    date_visited: imp.date,
    with_person: imp.with,
    notes: imp.notes,
    details: imp.details,
    rating: imp.rating || 0,
  };
  // Always include bottle fields on update so user can clear them.
  // null values explicitly clear the column; '' becomes null.
  patch.vintage  = (imp.vintage  === '' || imp.vintage  == null) ? null : imp.vintage;
  patch.region   = imp.region   == null ? null : imp.region;
  patch.country  = imp.country  == null ? null : imp.country;
  patch.varietal = imp.varietal == null ? null : imp.varietal;
  patch.abv      = (imp.abv === '' || imp.abv == null) ? null : imp.abv;
  const { error } = await supabase
    .from('swishd_impressions')
    .update(patch)
    .eq('id', id);
  return !error;
}

// ── CONNECTIONS ───────────────────────────────────────────
export async function saveConnection(profile, connectedAt, lastInteraction) {
  const uid = await getUserId();
  if (!uid) return false;
  const { error } = await supabase
    .from('swishd_connections')
    .upsert({
      user_id: uid,
      profile_name: profile.name,
      profile_data: profile,
      connected_at: connectedAt,
      last_interaction: lastInteraction
    }, { onConflict: 'user_id,profile_name' });
  return !error;
}

export async function loadConnections() {
  const uid = await getUserId();
  if (!uid) return [];
  const { data } = await supabase
    .from('swishd_connections')
    .select('*')
    .eq('user_id', uid)
    .order('last_interaction', { ascending: false });
  return (data || []).map(d => ({
    profile: d.profile_data,
    connectedAt: d.connected_at,
    lastInteraction: d.last_interaction
  }));
}

// ── SCAN HISTORY (legacy debug log) ─────────────────────────
// Kept for backward compat and edited-vs-extracted diff logging.
export async function saveScan(menuText, aiResponse, venue) {
  const uid = await getUserId();
  if (!uid) return;
  await supabase
    .from('swishd_scans')
    .insert({
      user_id: uid,
      menu_text: menuText,
      ai_response: aiResponse,
      venue: venue || ''
    });
}

// ── MENU SCANS + ITEMS (extraction is permanent) ───────────────────────
// Three-step flow on every scan:
//   1. createMenuScan({source, raw_text, venue_name}) → returns scan_id
//   2. insertMenuItems(scan_id, items[]) → returns inserted/existing menu_items rows
//   3. saveRanking(scan_id, food_context, palate_vector, ranked_items[]) → ephemeral snapshot
//
// Key invariant: menu_items.extraction_hash is unique per scan, so retries are idempotent.
// Hash is computed client-side via extractionHash.js before calling insertMenuItems.

export async function createMenuScan({ source = 'menu', rawText = '', venueName = '' }) {
  const uid = await getUserId();
  if (!uid) return null;
  const { data, error } = await supabase
    .from('menu_scans')
    .insert({
      user_id: uid,
      source,
      raw_text: rawText,
      venue_name: venueName || null,
    })
    .select('id')
    .single();
  if (error) {
    console.error('createMenuScan failed:', error);
    return null;
  }
  return data?.id || null;
}

// items: array of { name, price, category, producer, vintage, region, country, varietal, abv, sub_category, normalized_name, extraction_hash }
// Returns: array of menu_items rows (id + name, in input order, with deduplication applied)
export async function insertMenuItems(scanId, items) {
  if (!scanId || !items || items.length === 0) return [];
  const rows = items.map(it => ({
    scan_id: scanId,
    name: it.name,
    price: (it.price === null || it.price === undefined) ? null : Number(it.price),
    category: it.category || null,
    producer: it.producer || null,
    vintage: (it.vintage === null || it.vintage === undefined) ? null : Number(it.vintage),
    region: it.region || null,
    country: it.country || null,
    varietal: it.varietal || null,
    abv: (it.abv === null || it.abv === undefined) ? null : Number(it.abv),
    sub_category: it.sub_category || null,
    normalized_name: it.normalized_name,
    extraction_hash: it.extraction_hash,
  }));
  // upsert with on_conflict ignores duplicates per the unique (scan_id, extraction_hash) constraint.
  // We then re-select to get all rows (inserted + previously-existing) for this scan.
  const { error } = await supabase
    .from('menu_items')
    .upsert(rows, { onConflict: 'scan_id,extraction_hash', ignoreDuplicates: true });
  if (error) {
    console.error('insertMenuItems failed:', error);
    return [];
  }
  // Re-fetch the canonical rows for this scan (covers both newly inserted and pre-existing).
  const { data, error: selErr } = await supabase
    .from('menu_items')
    .select('*')
    .eq('scan_id', scanId);
  if (selErr) {
    console.error('insertMenuItems re-select failed:', selErr);
    return [];
  }
  return data || [];
}

// Load all items for a previously-completed scan. Used for food re-ranking without re-extraction.
export async function loadMenuItems(scanId) {
  if (!scanId) return [];
  const { data, error } = await supabase
    .from('menu_items')
    .select('*')
    .eq('scan_id', scanId)
    .order('extracted_at', { ascending: true });
  if (error) return [];
  return data || [];
}

// Save a ranking snapshot. ranked: array of { menu_item_id, match_score, rank_position, reason }.
// Returns the new ranking_id.
export async function saveRanking({ scanId, foodContext = null, palateVector = null, rankedItems = [] }) {
  if (!scanId) return null;
  const uid = await getUserId();
  if (!uid) return null;
  const { data, error } = await supabase
    .from('menu_rankings')
    .insert({
      scan_id: scanId,
      user_id: uid,
      food_context: foodContext,
      palate_vector_snapshot: palateVector,
    })
    .select('id')
    .single();
  if (error) {
    console.error('saveRanking failed:', error);
    return null;
  }
  const rankingId = data?.id;
  if (!rankingId || rankedItems.length === 0) return rankingId;
  const rows = rankedItems
    .filter(r => r.menu_item_id)
    .map(r => ({
      ranking_id: rankingId,
      menu_item_id: r.menu_item_id,
      match_score: typeof r.match_score === 'number' ? r.match_score : 0,
      rank_position: r.rank_position,
      reason: r.reason || null,
    }));
  if (rows.length > 0) {
    const { error: itemsErr } = await supabase
      .from('menu_ranking_items')
      .insert(rows);
    if (itemsErr) console.error('saveRanking items insert failed:', itemsErr);
  }
  return rankingId;
}

// LEGACY — kept temporarily until all callers migrate. Returns null.
export async function saveMenuSnapshot() {
  console.warn('saveMenuSnapshot is deprecated; use createMenuScan + insertMenuItems');
  return null;
}

export async function loadMenuSnapshot(menuId) {
  if (!menuId) return null;
  const { data } = await supabase.from('menu_scans').select('*').eq('id', menuId).single();
  return data || null;
}

// ── DRINK FLAVOR BLUEPRINTS (Step 5) ─────────────────────────
// Used by ranking (increment 2). Cache is shared across all users —
// "Negroni" tastes like "Negroni" regardless of who's drinking it.
export function normalizeDrinkKey(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // strip accents
    .replace(/[^a-z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export async function loadDrinkFlavor(name) {
  const key = normalizeDrinkKey(name);
  if (!key) return null;
  const { data, error } = await supabase
    .from('swishd_drink_flavors')
    .select('*')
    .eq('drink_key', key)
    .maybeSingle();
  if (error) return null;
  return data;
}

export async function loadDrinkFlavorsBatch(names) {
  const keys = (names || []).map(normalizeDrinkKey).filter(Boolean);
  if (keys.length === 0) return {};
  const { data, error } = await supabase
    .from('swishd_drink_flavors')
    .select('*')
    .in('drink_key', keys);
  if (error) return {};
  const out = {};
  (data || []).forEach(row => { out[row.drink_key] = row; });
  return out;
}

export async function saveDrinkFlavor({ name, category, vector, source = 'ai' }) {
  const key = normalizeDrinkKey(name);
  if (!key) return false;
  const { error } = await supabase
    .from('swishd_drink_flavors')
    .upsert({
      drink_key: key,
      display_name: name,
      category: category || 'unknown',
      vector,
      source,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'drink_key' });
  return !error;
}

// ── FOOD FLAVOR CACHE (Increment 5) ───────────────────────────
// Mirrors swishd_drink_flavors. Cached per dish name across users.
export function normalizeFoodKey(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export async function loadFoodFlavor(name) {
  const key = normalizeFoodKey(name);
  if (!key) return null;
  const { data, error } = await supabase
    .from('swishd_food_flavors')
    .select('*')
    .eq('food_key', key)
    .maybeSingle();
  if (error) return null;
  return data;
}

export async function saveFoodFlavor({ name, vector, source = 'ai' }) {
  const key = normalizeFoodKey(name);
  if (!key) return false;
  const { error } = await supabase
    .from('swishd_food_flavors')
    .upsert({
      food_key: key,
      display_name: name,
      vector,
      source,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'food_key' });
  return !error;
}

// ── BEHAVIORAL SIGNALS (Sub-scope 1 of 5) ─────────────────────
// Internal-only learning tables. Three append-only event streams.
// All signal writes are fire-and-forget — failures are logged, never block UI.

export async function writeSelectionSignal({ scanId, menuItemId, rankingId, foodContext, signalType }) {
  const userId = await getUserId();
  if (!userId) return false;
  if (!signalType) return false;
  const { error } = await supabase
    .from('swishd_selection_signals')
    .insert({
      user_id: userId,
      scan_id: scanId || null,
      menu_item_id: menuItemId || null,
      ranking_id: rankingId || null,
      food_context: foodContext || null,
      signal_type: signalType,
    });
  if (error) console.error('writeSelectionSignal failed:', error);
  return !error;
}

export async function writeSessionEvent({ scanId, eventType, meta }) {
  const userId = await getUserId();
  if (!userId) return false;
  if (!eventType) return false;
  const { error } = await supabase
    .from('swishd_session_events')
    .insert({
      user_id: userId,
      scan_id: scanId || null,
      event_type: eventType,
      meta: meta || null,
    });
  if (error) console.error('writeSessionEvent failed:', error);
  return !error;
}

export async function writeInteractionEvent({ eventName, properties, scanId }) {
  const userId = await getUserId();
  if (!userId) return false;
  if (!eventName) return false;
  const { error } = await supabase
    .from('swishd_interaction_events')
    .insert({
      user_id: userId,
      event_name: eventName,
      properties: properties || null,
      scan_id: scanId || null,
    });
  if (error) console.error('writeInteractionEvent failed:', error);
  return !error;
}

export default supabase;
